<template>
  <div>
    <div class="comment-form-info-message" v-if="!isSubmitting && message">
      <InfoMessage :type="message.success ? 'info' : 'error'" :title="message.message" centered />
    </div>
    <form v-else
          enctype="multipart/form-data"
          class="comment-form"
          :class="{ 'comment-form--answer': isAnswer }">
      <div class="comment-form__star-wrap" v-if="!isAnswer">
        <span class="comment-form__star-label">{{ $t('review.your.rating') }}</span>
        <StarRating
          :value="rating"
          size="big"
          @setRating="handleRating"
          @startOver="handleMouseover"
          @startOut="handleMouseout"
        />
      </div>
      <div class="comment-form__item">
        <CustomTextarea
          name="comment"
          :placeholder="$t('your.comment')"
          :label="isAnswer ? '' : $t('review.comment')"
          v-model="textarea.comment"
          :is-over="overText.comment"
          :is-error="errors.comment"
          @input="handleComment($event, 'comment')"
        />
        <span
          class="comment-form__comment-max-symbols"
          :class="{ 'comment-form__comment-max-symbols--without-offset': overText.comment }"
        ><span :class="{ 'is-over': overText.comment }">{{ currentLength.comment }}</span> /
          {{ maxLength.comment }}</span
        >
      </div>
      <div class="comment-form__item" v-if="!isAnswer">
        <CustomTextarea
          name="reasonForPurchase"
          :placeholder="$t('your.reason.for.purchase')"
          :label="isAnswer ? '' : $t('review.reason.for.purchase')"
          v-model="textarea.reasonForPurchase"
          :is-over="overText.reasonForPurchase"
          @input="handleComment($event, 'reasonForPurchase')"
        />
        <span class="comment-form__comment-max-symbols"
        ><span :class="{ 'is-over': overText.reasonForPurchase }">{{ currentLength.reasonForPurchase }}</span> /
          {{ maxLength.reasonForPurchase }}</span
        >
      </div>
      <FileInput
        :mainText="$t('review_add_photo')"
        :secondaryText="$t('review_add_photo_text')"
        @changeFiles="images = $event"
        ref="fileInput"
        name="images"
        :formats="['jpg', 'jpeg', 'png', 'gif']"
        :max-file-size="5242880"
        :max-files-count="10"
        class="comment-form__item"
        v-if="!isAnswer"
      />
      <VideoInput @changeVideos="videos = $event"
                  ref="videoInput"
                  class="comment-form__item"
                  v-if="!isAnswer" />
      <div class="comment-form__btn-wrap">
        <Button v-if="isAnswer" type="gray" @click.native="hideAnswerForm">
          {{ $t('comment.form.cancel') }}
        </Button>
        <Button
          class="comment-form__btn"
          @click.native="submitComment"
          :is-disabled="isSubmitting"
          :is-native-disabled="isSubmitting"
        >
          {{ $t('comment.form.submit') }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CustomTextarea from '@/components/inputs/CustomTextarea'
import Button from '@/components/inputs/Button'
import StarRating from '@/components/product/StarRating'
import FileInput from '../inputs/FileInput'
import VideoInput from '@/components/inputs/VideoInput'
import InfoMessage from '../helpers/InfoMessage'

export default {
  name: 'CommentsForm',
  data () {
    return {
      tempRating: 0,
      rating: 5,
      images: [],
      videos: [],
      textarea: {
        comment: '',
        reasonForPurchase: ''
      },
      overText: {
        comment: false,
        reasonForPurchase: false
      },
      currentLength: {
        comment: 0,
        reasonForPurchase: 0
      },
      maxLength: {
        comment: 2000,
        reasonForPurchase: 200
      },
      errors: {
        comment: false,
        reasonForPurchase: false
      },
      isSubmitting: false,
      message: null
    }
  },
  components: {
    VideoInput,
    Button,
    CustomTextarea,
    StarRating,
    FileInput,
    InfoMessage
  },
  props: {
    parentId: {
      type: Number,
      default: null
    },
    productId: {
      type: Number,
      default: null
    },
    type: {
      type: String,
      required: true
    },
    endpoint: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapState({
      // productId: state => state.productInfo.productInfo.productId
    }),
    isAnswer () {
      return this.type === this.$constants.COMMENTS.FORM_TYPE_ANSWER
    },
    isProductComments () {
      return this.type === this.$constants.COMMENTS.FORM_TYPE_PRODUCT
    },
    isCompanyComments () {
      return this.type === this.$constants.COMMENTS.FORM_TYPE_COMPANY
    },
    isValidComment () {
      const spaces = this.textarea.comment.replace(/^\s+|\s+$/g, '')

      return (
        this.textarea.comment &&
        !!spaces &&
        this.currentLength.comment <= this.maxLength.comment &&
        !this.overText.comment
      )
    },
    isValidReason () {
      const spaces = this.textarea.comment.replace(/^\s+|\s+$/g, '')

      return !this.overText.reasonForPurchase && !!spaces
    }
  },
  methods: {
    ...mapActions('comments', ['POST_NEW_COMMENT']),
    hideAnswerForm () {
      this.$emit('hideAnswerForm')
    },
    handleMouseover (index) {
      this.tempRating = this.rating
      this.rating = index
      return this.rating
    },
    handleMouseout () {
      this.rating = this.tempRating
      return this.rating
    },
    handleRating (value) {
      this.tempRating = +value
      this.rating = +value
      return this.rating
    },
    handleComment (value, el) {
      const lineBreaks = (value.match(/\n/g) || []).length

      this.currentLength[el] = this.textarea[el].length + lineBreaks

      this.overText[el] = this.currentLength[el] > this.maxLength[el]

      this.textarea[el] = value
    },
    getFormData () {
      let data = new FormData()

      if (this.parentId) data.append('parentId', this.parentId)
      if (this.productId) data.append('productId', this.productId)
      if (this.textarea.comment) data.append('comment', this.textarea.comment)
      if (this.textarea.reasonForPurchase) data.append('reason_for_purchase', this.textarea.reasonForPurchase)
      if (this.rating) data.append('rating', this.rating)

      this.videos.forEach(video => {
        data.append('videos[]', video)
      })

      this.images.forEach(file => {
        data.append('images[]', file)
      })

      return data
    },
    clearForm () {
      this.textarea = {
        comment: '',
        reasonForPurchase: ''
      }

      this.message = null

      if (this.$refs.fileInput) this.$refs.fileInput.clearFiles()
      if (this.$refs.videoInput) this.$refs.videoInput.clearAll()
    },
    submitComment () {
      const formData = this.getFormData()
      const isValidComment = this.isValidComment
      const isValidReason = this.isValidReason

      this.errors.comment = !isValidComment
      this.errors.reasonForPurchase = !isValidReason

      if (isValidComment && isValidReason) {
        this.isSubmitting = true

        this.POST_NEW_COMMENT({ formData, endpoint: this.endpoint })
          .then(res => {
            this.message = res
          })
          .catch(err => {
            this.message = err
          })
          .finally(() => {
            this.isSubmitting = false
            this.$emit('submitted')
          })
      }
    }
  },
  destroyed () {
    this.clearForm()
  }
}
</script>

<style scoped lang="sass">

.comment-form__item
  margin-bottom: 30px
  position: relative

.comment-form__star-wrap
  display: flex
  align-items: center
  flex-flow: row wrap
  margin: 40px 0

.comment-form__star-label
  font-size: 14px
  margin-right: 30px

.comment-form__btn-wrap
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: center
  margin-bottom: 30px

.comment-form__comment-max-symbols
  display: block
  width: 100%
  color: $placeholder-color
  font-size: 12px
  font-weight: 400
  text-align: right
  position: absolute
  bottom: -15px

  &--without-offset
    bottom: 1px

.is-over
  color: $main-color

.comment-form--answer
  .comment-form__star-wrap
    margin-bottom: 20px

  .comment-form__btn-wrap
    justify-content: flex-end

  .comment-form__btn
    margin-left: 20px

.comment-form-info-message
  margin-bottom: 20px
</style>
